// Same-origin JSON API client. Sends the session cookie automatically
// (credentials: 'include' is implicit same-origin) and echoes the CSRF token
// from the XSRF-TOKEN cookie on mutating requests.

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

interface RequestOptions {
  method?: string
  body?: unknown
  headers?: Record<string, string>
  signal?: AbortSignal
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET'
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers }

  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
  if (!['GET', 'HEAD'].includes(method)) {
    const csrf = readCookie('XSRF-TOKEN')
    if (csrf) headers['X-XSRF-TOKEN'] = csrf
  }

  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    signal: opts.signal,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  })

  if (res.status === 204) return undefined as T
  const text = await res.text()
  const data = text ? (JSON.parse(text) as unknown) : undefined

  if (!res.ok) {
    const msg =
      (data as { message?: string } | undefined)?.message ?? `${res.status} ${res.statusText}`
    throw new ApiError(res.status, msg, data)
  }
  return data as T
}

// Multipart upload. The JSON `request` helper above always sets Content-Type: application/json,
// which breaks file uploads — the browser must set the multipart boundary itself. So this is a
// separate path that posts a FormData body (no Content-Type header) while keeping the same CSRF
// echo and same-origin credential behavior.
async function upload<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const csrf = readCookie('XSRF-TOKEN')
  if (csrf) headers['X-XSRF-TOKEN'] = csrf

  const res = await fetch(path, { method: 'POST', headers, credentials: 'same-origin', body: form })

  if (res.status === 204) return undefined as T
  const text = await res.text()
  const data = text ? (JSON.parse(text) as unknown) : undefined
  if (!res.ok) {
    const msg =
      (data as { message?: string } | undefined)?.message ?? `${res.status} ${res.statusText}`
    throw new ApiError(res.status, msg, data)
  }
  return data as T
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  // Posts a single file as multipart/form-data under field `field` (default "file").
  uploadFile: <T>(path: string, file: File, field = 'file') => {
    const form = new FormData()
    form.append(field, file)
    return upload<T>(path, form)
  },
}
